import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Users, Trash2, Plus, Loader2, ShieldCheck, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../lib/LanguageContext';

export default function Beneficiaires() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ label: '', name: '', iban: '', bic: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/client/beneficiaries');
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error(t('beneficiaries.loadError'));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.label.trim() || !form.name.trim() || !form.iban.trim()) {
      toast.error(t('beneficiaries.requiredFields'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/client/beneficiaries', {
        label: form.label.trim(),
        name: form.name.trim(),
        iban: form.iban.replace(/\s+/g, '').toUpperCase(),
        bic: (form.bic || '').replace(/\s+/g, '').toUpperCase() || null,
      });
      toast.success(t('beneficiaries.added'));
      setShowAdd(false);
      setForm({ label: '', name: '', iban: '', bic: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('beneficiaries.addError'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/client/beneficiaries/${id}`);
      toast.success(t('beneficiaries.deleted'));
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    }
  };

  const handleUseBeneficiary = (b) => {
    // Sends the user to the transfer page with prefill via sessionStorage
    try {
      sessionStorage.setItem('bfi_beneficiary_prefill', JSON.stringify({
        beneficiaire_nom: b.name,
        beneficiaire_iban: b.iban,
        beneficiaire_bic: b.bic || '',
      }));
    } catch {}
    navigate('/dashboard/virement');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-blue-700"/> {t('beneficiaries.title')}</h1>
          <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('beneficiaries.subtitle')}</p>
        </div>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setShowAdd(true)} data-testid="btn-add-beneficiary">
          <Plus className="w-4 h-4 mr-1"/> {t('beneficiaries.addNew')}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden dark:bg-slate-900 dark:border-slate-700">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-3"><Users className="w-7 h-7"/></div>
            <h2 className="font-semibold">{t('beneficiaries.noBeneficiaries')}</h2>
            <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('beneficiaries.noBeneficiariesDesc')}</p>
            <Button className="mt-4 bg-blue-700" onClick={() => setShowAdd(true)}><Plus className="w-4 h-4 mr-1"/> {t('beneficiaries.addNew')}</Button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {items.map((b) => (
              <li key={b.id} className="p-4 flex items-center gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                  {(b.label || b.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{b.label} <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">— {b.name}</span></div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{b.iban}{b.bic ? ` • ${b.bic}` : ''}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleUseBeneficiary(b)} data-testid={`btn-use-${b.id}`}>
                    <Send className="w-3.5 h-3.5 mr-1"/> {t('beneficiaries.transfer')}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => setConfirmDel(b)} data-testid={`btn-del-${b.id}`}>
                    <Trash2 className="w-4 h-4"/>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Suggestion banner */}
      <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 rounded-2xl p-4 flex items-start gap-2">
        <ShieldCheck className="w-5 h-5 text-blue-700 mt-0.5 shrink-0"/>
        <div className="text-xs text-blue-900 dark:text-blue-200">
          {t('beneficiaries.securityNote')}
        </div>
      </div>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('beneficiaries.newBeneficiary')}</DialogTitle>
            <DialogDescription>{t('beneficiaries.newBeneficiaryDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>{t('beneficiaries.labelField')}</Label><Input value={form.label} onChange={(e) => upd('label', e.target.value)} placeholder={t('beneficiaries.labelPlaceholder')} data-testid="input-ben-label"/></div>
            <div><Label>{t('beneficiaries.fullName')}</Label><Input value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder={t('beneficiaries.namePlaceholder')} data-testid="input-ben-name"/></div>
            <div><Label>{t('transfer.iban')}</Label><Input value={form.iban} onChange={(e) => upd('iban', e.target.value.toUpperCase())} placeholder="FR76..." className="font-mono" data-testid="input-ben-iban"/></div>
            <div><Label>{t('beneficiaries.bicOptional')}</Label><Input value={form.bic} onChange={(e) => upd('bic', e.target.value.toUpperCase())} placeholder="ex: BNPAFRPP" data-testid="input-ben-bic"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>{t('common.cancel')}</Button>
            <Button className="bg-blue-700" onClick={submit} disabled={saving} data-testid="btn-save-ben">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Plus className="w-4 h-4 mr-1"/>} {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('beneficiaries.confirmDelete')}</DialogTitle>
            <DialogDescription>
              {confirmDel ? <>{t('beneficiaries.confirmDeleteDesc1')} <strong>{confirmDel.label}</strong> ({confirmDel.name}). {t('beneficiaries.confirmDeleteDesc2')}</> : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => remove(confirmDel.id)}><Trash2 className="w-4 h-4 mr-1"/> {t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
